import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { BRAND_INTEREST_OPTIONS } from "../config/brands";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
    name: "",
    email: "",
    phone: "",
    company: "",
    brand_of_interest: "",
    message: "",
};

const inputClass =
    "min-h-[48px] border-white/10 bg-white/[0.03] text-slate-100 placeholder:text-slate-600 focus-visible:ring-amber-400/50 focus-visible:border-amber-400/40";

export default function ContactSection() {
    const [form, setForm] = useState(INITIAL);
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            setSent(true);
            setForm(INITIAL);
            toast.success("Message received. Truth Excellence Group will be in touch.");
        } catch (err) {
            toast.error("Something went wrong sending your message. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="contact" className="relative scroll-mt-24 px-5 py-24 sm:py-32" aria-label="Contact">
            <div
                className="pointer-events-none absolute inset-0"
                aria-hidden="true"
                style={{
                    background:
                        "radial-gradient(50% 40% at 20% 30%, rgba(212,175,55,0.07), transparent 70%), radial-gradient(40% 35% at 85% 70%, rgba(0,122,255,0.07), transparent 70%)",
                }}
            />
            <div className="relative mx-auto grid max-w-6xl gap-14 lg:grid-cols-2 lg:gap-20">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                    <p className="eyebrow mb-4">Contact</p>
                    <h2 className="font-display text-3xl leading-tight font-medium tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
                        Let's Build Something <span className="text-gold-gradient">Excellent.</span>
                    </h2>
                    <p className="font-editorial mt-6 text-xl leading-relaxed text-slate-400 italic sm:text-2xl">
                        Connect with Truth Excellence Group to explore partnerships, services,
                        live experiences, and new opportunities.
                    </p>
                    <div className="gold-hairline mt-10 w-40" aria-hidden="true" />
                    <p className="font-mono-lux mt-10 text-[10px] leading-loose tracking-[0.3em] text-slate-500 uppercase">
                        Truth Excellence Group LLC
                        <br />
                        Building Wealth. Protecting Legacies.
                        <br />
                        Creating Opportunity.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    className="relative overflow-hidden rounded-2xl border border-amber-400/15 bg-[#0A0B10]/80 p-6 shadow-[0_30px_80px_rgba(0,0,0,0.5)] backdrop-blur-xl sm:p-8"
                >
                    <div
                        className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
                        style={{
                            background:
                                "linear-gradient(90deg, transparent, rgba(212,175,55,0.7), rgba(0,122,255,0.5), transparent)",
                        }}
                        aria-hidden="true"
                    />
                    {sent ? (
                        <div
                            className="flex min-h-[420px] flex-col items-center justify-center text-center"
                            data-testid="teg-contact-success-toast"
                        >
                            <CheckCircle2 className="mb-5 text-amber-300" size={44} strokeWidth={1.5} />
                            <h3 className="font-display text-2xl font-medium text-slate-100">Message Received.</h3>
                            <p className="mt-3 max-w-xs text-sm leading-relaxed text-slate-400">
                                Thank you for reaching out. Truth Excellence Group will be in
                                touch soon.
                            </p>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setSent(false)}
                                data-testid="teg-contact-send-another-btn"
                                className="mt-8 min-h-[44px] rounded-full border-amber-400/50 bg-transparent px-8 text-[11px] tracking-[0.25em] text-amber-300 uppercase hover:bg-amber-400/10"
                            >
                                Send Another Message
                            </Button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5" data-testid="teg-contact-form">
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="contact-name" className="font-mono-lux text-[10px] tracking-[0.25em] text-slate-400 uppercase">
                                        Name *
                                    </Label>
                                    <Input
                                        id="contact-name"
                                        required
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder="Your full name"
                                        className={inputClass}
                                        data-testid="teg-contact-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="contact-email" className="font-mono-lux text-[10px] tracking-[0.25em] text-slate-400 uppercase">
                                        Email *
                                    </Label>
                                    <Input
                                        id="contact-email"
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="you@example.com"
                                        className={inputClass}
                                        data-testid="teg-contact-email-input"
                                    />
                                </div>
                            </div>
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="contact-phone" className="font-mono-lux text-[10px] tracking-[0.25em] text-slate-400 uppercase">
                                        Phone <span className="text-slate-600">(optional)</span>
                                    </Label>
                                    <Input
                                        id="contact-phone"
                                        type="tel"
                                        value={form.phone}
                                        onChange={set("phone")}
                                        placeholder="(555) 000-0000"
                                        className={inputClass}
                                        data-testid="teg-contact-phone-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="contact-company" className="font-mono-lux text-[10px] tracking-[0.25em] text-slate-400 uppercase">
                                        Company <span className="text-slate-600">(optional)</span>
                                    </Label>
                                    <Input
                                        id="contact-company"
                                        value={form.company}
                                        onChange={set("company")}
                                        placeholder="Organization"
                                        className={inputClass}
                                        data-testid="teg-contact-company-input"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="contact-brand" className="font-mono-lux text-[10px] tracking-[0.25em] text-slate-400 uppercase">
                                    Brand of Interest *
                                </Label>
                                <Select
                                    required
                                    value={form.brand_of_interest}
                                    onValueChange={(v) => setForm((f) => ({ ...f, brand_of_interest: v }))}
                                >
                                    <SelectTrigger
                                        id="contact-brand"
                                        className={`${inputClass} w-full`}
                                        data-testid="teg-contact-brand-select"
                                        aria-label="Brand of interest"
                                    >
                                        <SelectValue placeholder="Select a brand" />
                                    </SelectTrigger>
                                    <SelectContent className="border-white/10 bg-[#0A0B10] text-slate-100">
                                        {BRAND_INTEREST_OPTIONS.map((opt) => (
                                            <SelectItem key={opt} value={opt} className="focus:bg-amber-400/10 focus:text-amber-200">
                                                {opt}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="contact-message" className="font-mono-lux text-[10px] tracking-[0.25em] text-slate-400 uppercase">
                                    Message *
                                </Label>
                                <Textarea
                                    id="contact-message"
                                    required
                                    rows={5}
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder="Tell us about the opportunity..."
                                    className={`${inputClass} min-h-[120px] resize-y`}
                                    data-testid="teg-contact-message-input"
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={submitting}
                                data-testid="teg-contact-submit-btn"
                                className="min-h-[52px] w-full rounded-full bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 text-[12px] font-semibold tracking-[0.28em] text-[#050507] uppercase shadow-[0_10px_40px_rgba(212,175,55,0.3)] transition-all duration-300 hover:shadow-[0_10px_55px_rgba(212,175,55,0.5)] disabled:opacity-60"
                            >
                                {submitting ? "Sending..." : "Submit"}
                            </Button>
                        </form>
                    )}
                </motion.div>
            </div>
        </section>
    );
}
